import React, {useEffect, useState, memo} from "react";
import {csv, isStaticSite, fs} from '../electron-modules';
import ListItem from "@material-ui/core/ListItem";
import CloudIcon from "@material-ui/icons/Cloud";
import InfoIcon from "@material-ui/icons/Info";
import PanoramaIcon from '@material-ui/icons/Panorama';
import PanoramaOutlinedIcon from '@material-ui/icons/PanoramaOutlined';
import ListItemText from "@material-ui/core/ListItemText";
import {
  Avatar,
  Checkbox,
  DialogContent,
  ListItemSecondaryAction,
  MenuItem,
  Typography
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ErrorIcon from '@material-ui/icons/Error';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import WarningIcon from '@material-ui/icons/Warning';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import Satellite from '@material-ui/icons/Satellite';
import {DenseMenu, DraggableDialog, getAnchorPosition, initialMenuState, LinearProgressWithLabel, MenuDivider, PromptDialog, TextDialog} from "../components";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {
  AssetType,
  toggleVisibility,
  changeName,
  deleteAsset,
  Asset,
  CameraData,
  changeTexture,
  TagData
} from "../redux/assets-slice";
import {useDispatch} from 'react-redux';
import {exportDialogCSV} from '../utilities';
import {
  DenseIcon,
  FontAwesomeIcon,
  ArrowTooltip
} from '../components';
import clsx from 'clsx';
import {Viewer} from "../viewer/js/main";
import {toast} from "../app";
import { TagDetailsDialog, TagIconDialog } from ".";
import { getTagTexturePath } from "../viewer/textures/tag-icons";
import { TagItem } from "../viewer/js/tags";
import { isEqual } from 'lodash';
import { useTranslation } from "react-i18next";
import { FunctionsMenu } from "../functions";

const updateInterval = 200;

interface AssetState {
  assetPercent: number
  assetError: boolean
  assetIsLarge: boolean
}

const initialAssetState = {
  assetPercent: 0,
  assetError: false,
  assetIsLarge: false,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    floatRight: {
      float: "right"
    },
    itemList: {
      paddingLeft: theme.spacing(4),
      display: "inline-block"
    },
    itemText: {
      wordBreak: "break-all",
      overflow: "hidden"
    },
    assetMargins: {
      margin: '0px 20px 0px 0px'
    },
    assetError: {
      color: theme.palette.error.main,
      fontWeight: "bold"
    },
    assetWarning: {
      color: theme.palette.warning.main,
      fontWeight: "bold"
    },
    assetInfo: {
      display: 'flex',
      margin: '0px'
    },
    assetCheckbox: {
      position: "relative",
      transform: "translateY(0%)",
    },
    checkboxParent: {
      padding: "0px",
      height: "100%"
    },
    percentLabel: {
      textAlign: "right"
    },
    fileInfoHeading: {
      paddingTop: theme.spacing(2.0)
    },
    fileInfoText: {
      display: "block"
    },
    fileInfoContent: {
      maxHeight: "400px"
    },
    tagAvatar: {
      width: "20px",
      height: "20px",
      borderRadius: theme.spacing(0)
    },
    titleClose: {
      position: 'absolute',
      right: theme.spacing(0),
      color: theme.palette.grey[500],
    },
  }),
);

export const exportCamerasToCSV = (cameras, csvPath): Promise<boolean> => {
  return new Promise(resolve => {
    let csvEncompassOut = [];
    csvEncompassOut.push(['Filename', 'X', 'Y', 'Z','Roll', 'Pitch', 'Yaw']);

    cameras.forEach(camera => {
      let position = camera.position.value;
      let rotation = camera.rotation.eulerDegrees;

      csvEncompassOut.push([
        camera.name,
        position.x, position.y, position.z,
        rotation.x, rotation.y, rotation.z
      ]);
    });

    csv.writeToString(csvEncompassOut).then(text => {
      try {
        fs.writeFileSync(csvPath, text, 'utf8');
        console.log('Done writing');
        resolve(true);
      } catch(err) {
        console.log(err);
        resolve(false);
      }
    });
  });
};

const exportTagsToCSV = (tags: TagItem[], csvPath): Promise<boolean> => {
  return new Promise(resolve => {
    let tagCsvOut = [];

    const hasHeight = tags.filter(tag => !tag.isAerial).length > 0;

    const header = [
      ...['Name', 'Easting', 'Northing'],
      ...hasHeight ? ['Elevation'] : [],
      'Comment'
    ];

    tagCsvOut.push(header);

    tags.forEach(tag => {
      const row = [
        tag.name,
        ...tag.position.array,
        ...tag.isAerial ? [''] : [],
        tag.comment
      ];
      tagCsvOut.push(row);
    });

    csv.writeToString(tagCsvOut).then(text => {
      try {
        fs.writeFileSync(csvPath, text, 'utf8');
        console.log('Done writing');
        resolve(true);
      } catch(err) {
        console.log(err);
        resolve(false);
      }
    });
  });
};

const LasFileInfoDialog = (props) => {
  const asset = props.asset as Asset;
  const {open, onClose, data} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const hasVLRSData = data.vlrs_data;

  let vlrsKeys = null;
  if (hasVLRSData) {
    vlrsKeys = Object.keys(data.vlrs_data);
    vlrsKeys.sort();
  }

  return (
    <DraggableDialog
      open={open}
      title={t('asset.las-file-information')}
      onClose={onClose}
    >
      <DialogContent className={classes.fileInfoContent}>
        <Typography gutterBottom variant="subtitle2">
          {t('asset.general-attributes')}
        </Typography>

        <Typography variant="caption">
          <b>{t('asset.file-name')}: </b> {asset.name}
        </Typography>

        <Typography gutterBottom variant="subtitle2" className={classes.fileInfoHeading}>
          {t('asset.point-information')}
        </Typography>
        <Typography variant="caption" className={classes.fileInfoText}>
          <b>{t('asset.point-count')}: </b> {data.points.count}
        </Typography>
        <Typography variant="caption" className={classes.fileInfoText}>
          <b>{t('asset.x-range')}: </b> [{data.points.x_min.toFixed(2)}, {data.points.x_max.toFixed(2)}]
        </Typography>
        <Typography variant="caption" className={classes.fileInfoText}>
          <b>{t('asset.y-range')}: </b> [{data.points.y_min.toFixed(2)}, {data.points.y_max.toFixed(2)}]
        </Typography>
        <Typography variant="caption" className={classes.fileInfoText}>
          <b>{t('asset.z-range')}: </b> [{data.points.z_min.toFixed(2)}, {data.points.z_max.toFixed(2)}]
        </Typography>

        <Typography gutterBottom variant="subtitle2" className={classes.fileInfoHeading}>
          {t('asset.data-format')}
        </Typography>
        <Typography variant="caption" className={classes.fileInfoText}>
          <b>{t('asset.las-version')}: </b> {data.attributes.version}
        </Typography>
        <Typography variant="caption" className={classes.fileInfoText}>
          <b>{t('asset.record-id')}: </b> {data.attributes.record_id}
        </Typography>
        <Typography variant="caption" className={classes.fileInfoText}>
          <b>{t('asset.contains-timestamp')}: </b> {data.attributes.has_time_stamp ? t('general.yes') : t('general.no')}
        </Typography>
        <Typography variant="caption" className={classes.fileInfoText}>
          <b>{t('asset.contains-rgb')}: </b> {data.attributes.has_rgb ? t('general.yes') : t('general.no')}
        </Typography>
        <Typography variant="caption" className={classes.fileInfoText}>
          <b>{t('asset.contains-intensity')}: </b> {data.attributes.has_intensity ? t('general.yes') : t('general.no')}
        </Typography>

        <Typography gutterBottom variant="subtitle2" className={classes.fileInfoHeading}>
          {t('asset.vlrs-metadata')}
        </Typography>

        {hasVLRSData && (<React.Fragment>
          {vlrsKeys.map((key, index) => {
            return (
              <Typography key={index} variant="caption" className={classes.fileInfoText}>
                <b>{key}: </b> {data.vlrs_data[key]}
              </Typography>
            );
          })}
        </React.Fragment>)}

        {/* Nothing found in VLRS */}
        {!hasVLRSData && (<React.Fragment>
          <Typography variant="caption" className={classes.fileInfoText}>
            <b>{t('asset.no-data-found')}</b>
          </Typography>
        </React.Fragment>)}

      </DialogContent>
    </DraggableDialog>
  );
};

export const AssetItem = memo((props: any) => {
  const viewer = props.viewer as Viewer;
  const asset = props.asset as Asset;
  const {setDraggingAsset} = props;

  const dispatch = useDispatch();
  const classes = useStyles();
  const {t} = useTranslation();

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [fileInfoOpen, setFileInfoOpen] = useState(false);
  const [iconUpdateOpen, setIconUpdateOpen] = useState(false);
  const [menuState, setMenuState] = useState({...initialMenuState});
  const [assetState, setAssetState] = useState({
    ...initialAssetState
  });

  const isPanoramic = asset.type === AssetType.Panoramic;
  const isPlanar = asset.type === AssetType.Planar;
  const isEncompassCloud = asset.type === AssetType.Encompass;
  const isPotreeCloud = asset.type === AssetType.Potree;
  const isLASFile = asset.type === AssetType.LAS;
  const isE57File = asset.type === AssetType.E57Points;
  const isOrtho = asset.type === AssetType.OrthoMosaic;
  const isTag = asset.type === AssetType.Tag;
  const isIFC = asset.type === AssetType.IFC;
  const isSHP = asset.type === AssetType.SHP;
  const isDXF = asset.type === AssetType.DXF;

  const isModelFile = isIFC || isSHP || isDXF;
  const isCamera = isPanoramic || isPlanar;
  const isPointCloud = isLASFile || isEncompassCloud || isE57File || isPotreeCloud;
  const texturePath = isTag ? getTagTexturePath(asset.texture) : null;

  /** Handle asset percentage / state changes */
  useEffect(() => {
    // Create interval to update state
    const interval = setInterval(() => {
      updateAssetState();
    }, updateInterval);

    return () => {
      clearInterval(interval);
    };
  }, [viewer, assetState]);

  useEffect(() => {
    // Update initial state
    updateAssetState();
  }, []);

  const updateAssetState = () => {
    if (!viewer) return;

    let newAssetState = {} as AssetState;

    if (isPointCloud) {
      newAssetState = viewer.getCloudStatus(asset.id);
    } else if (isModelFile) {
      newAssetState = viewer.getModelStatus(asset.id);
    }

    if (isEqual(assetState, newAssetState)) return;
    setAssetState(newAssetState);
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
    setMenuState({
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };

  const handleMenuClose = () => {
    setMenuState(initialMenuState);
  };

  const handleRenameCancel = () => {
    setRenameOpen(false);
  };

  const handleRenameSubmit = name => {
    dispatch(changeName({assetID: asset.id, name}));
    setRenameOpen(false);
  };

  const handleRenameClick = () => {
    handleMenuClose();
    setRenameOpen(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteOpen(true);
  };

  const handleIconChangeClick = () => {
    handleMenuClose();
    setIconUpdateOpen(true);
  };

  const handleTagCSVExport = (asset) => {
    handleMenuClose();
    const outputName = asset.name;

    exportDialogCSV(outputName).then(csvPath => {
      if (!csvPath) {
        return;
      }

      const tags = viewer.getTagsForCSV(asset.id);
      exportTagsToCSV(tags, csvPath);
    });
  };

  const handleImageCSVExport = (asset) => {
    handleMenuClose();
    const outputName = `${asset.name} corrected`;

    exportDialogCSV(outputName).then(csvPath => {
      if (!csvPath) {
        return;
      }

      const cameras = viewer.getCamerasForCSV(asset.id);
      exportCamerasToCSV(cameras, csvPath);
    });
  };

  const onAssetDelete = () => {
    dispatch(deleteAsset(asset.id));
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
  };

  const handleAssetClick = (event) => {
    const isCheckBox = (event.target.type === "checkbox");
    if (isCheckBox) return;

    if (isOrtho) {
      viewer?.snapToOrtho(asset.id);
    } else if (isPointCloud) {
      viewer?.snapToPointCloud(asset.id);
    } else if (isCamera) {
      viewer?.snapToImages(asset.id);
    } else if (isTag) {
      setDetailsDialogOpen(true);
    } else if (isModelFile) {
      viewer?.snapToModel(asset.id);
    }
  };

  const handleDragStart = () => {
    setDraggingAsset(asset.id);
  };

  const handleDragEnd = () => {
    setDraggingAsset(null);
  };

  const handleVisibilityClick = () => {
    dispatch(toggleVisibility(asset.id));
  };

  const handleFileInfoClick = () => {
    handleMenuClose();

    if (fileInfo) {
      setFileInfoOpen(true);
      return;
    };

    let info = viewer.getLasFileInfo(asset.id);
    if (!info) {
      toast.warning(t("toast.file-info-missing"));
      return;
    }

    setFileInfo(info);
    setFileInfoOpen(true);
  };

  const handleFileInfoCancel = () => {
    setFileInfoOpen(false);
  };

  const getAssetText = () => {
    if (isCamera || isTag) {
      let data = asset.data as CameraData[] | TagData[];
      return `(${data.length}) ${asset.name}`;
    }

    return asset.name;
  };

  const menuOpen = (menuState.mouseY !== null) && !isStaticSite;
  const anchorPosition = getAnchorPosition(menuState);
  const {assetPercent, assetError, assetIsLarge} = assetState;

  const isLargeCloud = isPointCloud
    && assetIsLarge
    && (assetPercent < 100)
    && !assetError;

  const showPercent = (isLASFile || isE57File || isModelFile)
    && asset.visible
    && (assetPercent < 100);

  const text = getAssetText();

  return (
    <div>
      <ListItem
        button
        draggable
        className={classes.itemList}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={handleAssetClick}
      >
        <div className={classes.assetInfo}>
          <DenseIcon centered>
            <React.Fragment>
              {/* Panoramic File */}
              {(isPanoramic) && (<PanoramaIcon fontSize="small"/>)}

              {/* Planar File */}
              {(isPlanar) && (<PanoramaOutlinedIcon fontSize="small"/>)}

              {/* Tag File */}
              {isTag && (<Avatar src={texturePath} className={classes.tagAvatar}/>)}

              {/* Orthomosaic File */}
              {(isOrtho) && (<Satellite fontSize="small"/>)}

              {/* Model File */}
              {isModelFile && <React.Fragment>
                {!assetError && (
                  <FontAwesomeIcon listIcon icon={"fa-cube"}/>
                )}

                {assetError && (
                  <ArrowTooltip title={t('asset.file-error')}>
                    <ErrorIcon  className={classes.assetError} fontSize="small"/>
                  </ArrowTooltip>
                )}
              </React.Fragment>}

              {/* Pointcloud File */}
              {isPointCloud && <React.Fragment>
                {(!assetError && !isLargeCloud) && (
                  <CloudIcon fontSize="small"/>
                )}

                {assetError && (
                  <ArrowTooltip title={t('asset.file-error')}>
                    <ErrorIcon  className={classes.assetError} fontSize="small"/>
                  </ArrowTooltip>
                )}

                {(isLargeCloud) && (
                  <ArrowTooltip title={t('asset.loading-large-point-cloud')}>
                    <WarningIcon className={classes.assetWarning} fontSize="small"/>
                  </ArrowTooltip>
                )}
              </React.Fragment>}

            </React.Fragment>
          </DenseIcon>

          <ListItemText
            className={clsx(classes.itemText, classes.assetMargins, {
              [classes.assetWarning]: isLargeCloud,
              [classes.assetError]: assetError
            })}
            primary={text}
          />

          <ListItemSecondaryAction
            className={classes.assetCheckbox}
          >
            <Checkbox
              edge="end"
              size="small"
              className={classes.checkboxParent}
              onChange={handleVisibilityClick}
              checked={asset.visible}
            />
          </ListItemSecondaryAction>
        </div>

        {/* File load percent */}
        {showPercent && (<div>
          <LinearProgressWithLabel
            value={assetPercent}
            color="secondary"
          />
        </div>)}

      </ListItem>

      <DenseMenu
        keepMounted
        open={menuOpen}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
      >
        {/** Functions submenu */}
        {!isStaticSite && <FunctionsMenu
          viewer={viewer}
          assets={[asset]}
          contextOpen={menuOpen}
          closeContextMenu={handleMenuClose}
          isPanoramic={isPanoramic}
          isLASFile={isLASFile}
        />}

        {!isStaticSite && <MenuDivider/>}

        {/** Export image csv */}
        {isPanoramic && !isStaticSite && <MenuItem onClick={() => {
          handleImageCSVExport(asset);
        }}>
          <DenseIcon>
            <ImportExportIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">
            {t('asset.export-corrected-csv')}
          </Typography>
        </MenuItem>}

        {/** Export tag csv */}
        {isTag && !isStaticSite && <MenuItem onClick={() => {
          handleTagCSVExport(asset);
        }}>
          <DenseIcon>
            <ImportExportIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">
            {t('asset.export-tag-csv')}
          </Typography>
        </MenuItem>}

        {/** Get LAS information */}
        {isLASFile && (<MenuItem onClick={handleFileInfoClick}>
          <DenseIcon>
            <InfoIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">
            {t('asset.file-info')}
          </Typography>
        </MenuItem>)}

        {/** Update icon */}
        {isTag && (<MenuItem onClick={handleIconChangeClick}>
          <DenseIcon>
            <PhotoLibraryIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">
            {t('asset.update-tag-icon')}
          </Typography>
        </MenuItem>)}

        {/** Rename asset */}
        <MenuItem onClick={handleRenameClick}>
          <DenseIcon>
            <EditIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">
            {t('asset.rename')}
          </Typography>
        </MenuItem>

        {/** Delete asset */}
        <MenuItem onClick={handleDeleteClick}>
          <DenseIcon>
            <DeleteIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">
            {t('asset.delete')}
          </Typography>
        </MenuItem>
      </DenseMenu>

      {isTag && (<TagIconDialog
        open={iconUpdateOpen}
        currentTexture={asset.texture}
        fileName={asset.name}
        onSubmit={(selected) => {
          dispatch(changeTexture({
            assetID: asset.id,
            texture: selected
          }));

          setIconUpdateOpen(false);
        }}
        onClose={() => {
          setIconUpdateOpen(false);
        }}
      ></TagIconDialog>)}

      {/* Discard measurements dialog */}
      <PromptDialog
        open={deleteOpen}
        onCancel={handleDeleteCancel}
        onSubmit={() => {
          onAssetDelete();
        }}
        title={t('asset.delete-title')}
        prompt={t('asset.delete-prompt', {
          name: asset.name
        })}
        button={t("buttons.delete")}
      />

      {/* Rename Dialog */}
      <TextDialog
        open={renameOpen}
        onCancel={handleRenameCancel}
        onSubmit={handleRenameSubmit}
        title={t('asset.rename-title')}
        prompt={t('asset.rename-prompt')}
        default={asset.name}
        label={t('asset.rename-label')}
      />

      {/* LAS file info Dialog */}
      {fileInfo && (<LasFileInfoDialog
        open={fileInfoOpen}
        onClose={handleFileInfoCancel}
        data={fileInfo}
        asset={asset}
      />)}

      {/* Tag info dialog */}
      {isTag && <TagDetailsDialog
        open={detailsDialogOpen}
        tags={asset.data}
        title={t("tags.details-title", {name: asset.name})}
        onClose={() => {
          setDetailsDialogOpen(false);
        }}
        viewer={viewer}
      />}
    </div>
  );
});
